import { Alert, Box, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { api, unwrap } from '../../../api/client';
import { useAuth } from '../../../auth/AuthProvider';
import { money } from '../../../lib/format';
import { useShop } from '../../../shop/ShopProvider';
import { contactKeys } from '../../contacts/model';
import { dec } from '../lib/decimal';
import { creditCheck, type CreditCustomer, type CreditState } from '../lib/tender';

/**
 * The customer's account as it stands now, fresh from the server (balances move with every sale).
 * Falls back to what the caller already has (a picked contact) while loading or offline.
 */
export function useCustomerAccount(
  id: string | null | undefined,
  fallback?: CreditCustomer | null,
): CreditCustomer | null {
  const q = useQuery({
    queryKey: contactKeys.detail(id ?? ''),
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
    staleTime: 0,
    retry: false,
  });
  if (!id) return null;
  // A contact from a saved document carries no balances (only id, name, phone): wait for the server.
  return q.data ?? (fallback?.balances ? fallback : null);
}

/**
 * The customer's balance for a receipt printed right after a sale: always fetched again on mount,
 * and null until that fetch is back (a balance from before the sale must not be printed) or offline.
 */
export function useAccountBalance(id: string | null | undefined): string | null {
  const q = useQuery({
    queryKey: contactKeys.detail(id ?? ''),
    queryFn: () => unwrap(api.GET('/contacts/{id}', { params: { path: { id: id ?? '' } } })),
    enabled: Boolean(id),
    refetchOnMount: 'always',
    retry: false,
  });
  if (!id || q.isFetching) return null;
  return q.data?.balances.receivable.signed ?? null;
}

/** Credit check for putting `adding` on the account, and whether this user may go over the limit. */
export function useCreditCheck(
  customer: CreditCustomer | null,
  adding: string,
): { check: CreditState | null; blocked: boolean } {
  const { can } = useAuth();
  const { format } = useShop();
  const check = creditCheck(customer, adding, format.currencyDecimals);
  return { check, blocked: !!check?.over && !can('sales.credit_override') };
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Typography className="tabular" sx={{ fontWeight: 700, color: tone }} noWrap>
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Due now, credit limit, credit left and the balance after this sale, with the over-limit warning
 * (a block without `sales.credit_override`, the server refuses it too).
 */
export function CustomerAccountCard({
  name,
  check,
  adding,
  blocked,
  dense,
}: {
  name: string;
  check: CreditState;
  /** What this sale puts on the account. */
  adding: string;
  blocked: boolean;
  dense?: boolean;
}) {
  const { format } = useShop();
  const due = dec(check.due);
  return (
    <Box
      data-testid="customer-account"
      sx={(t) => ({
        p: dense ? 1.5 : 2,
        borderRadius: '14px',
        border: `1px solid ${(t.vars ?? t).palette.divider}`,
        bgcolor: 'background.default',
      })}
    >
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {name}’s account
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1.25 }}>
        <Figure
          label={due < 0n ? 'In credit' : 'Due now'}
          value={money(due < 0n ? check.due.slice(1) : check.due, format)}
          tone={due > 0n ? 'warning.main' : undefined}
        />
        <Figure
          label="Credit limit"
          value={check.limit === null ? 'No limit' : money(check.limit, format)}
        />
        <Figure
          label="Credit left"
          value={check.left === null ? 'No limit' : money(check.left, format)}
          tone={check.left !== null && dec(check.left) < 0n ? 'error.main' : undefined}
        />
        <Figure
          label="After this sale"
          value={money(check.after, format)}
          tone={check.over ? 'error.main' : undefined}
        />
      </Box>
      {check.over && (
        <Alert severity={blocked ? 'error' : 'warning'} sx={{ mt: 1.5 }}>
          {money(adding, format)} on account goes over {name}’s credit limit.{' '}
          {blocked
            ? 'Take more payment now, or ask a manager to raise the limit.'
            : 'You can go over it: you have the credit override permission.'}
        </Alert>
      )}
    </Box>
  );
}
